import React from 'react';
import styled from 'styled-components';
import { ModalHeader } from '../../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { Modal } from '../../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';

const StyledText = styled(Text)(({ theme }) => ({
  color: theme.colors.neutral['600'],
  fontSize: theme.fontSizes.body,
  lineHeight: theme.lineHeights.body,
  fontWeight: 400,

  h3: {
    color: theme.colors.neutral['600'],
    fontSize: theme.fontSizes.body,
    lineHeight: theme.lineHeights.body,
    fontWeight: 600,
    marginBottom: 4,

    '+ p': {
      marginTop: 0,
    },
  },

  strong: {
    fontWeight: 600,
  },

  p: {
    marginBottom: 24,
  },

  ol: {
    paddingLeft: 16,

    ol: {
      paddingLeft: 24,
      marginBottom: 24,

      li: {
        marginBottom: 4,
      },
    },

    li: {
      marginBottom: 16,
    },
  },
}));

interface Props {
  onClose: () => void;
  onAccept: () => void;
}

export const TermsModal = ({ onClose, onAccept }: Props) => {
  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: 'BeFi20-terms--modal-header' }}
    >
      <ModalHeader as="h2" id="BeFi20-terms--modal-header">
        Orion BeFi20 Services Agreement
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <StyledText>
          <p>
            <strong>This Orion BeFi20 Services Agreement</strong> (“Agreement”) is made effective as
            of the date you (the “Customer”) click “I Accept” and elect to turn on the BeFi20
            Assessment Tool within Orion Planning (the “Effective Date”). By clicking “I Accept,”
            Customer agrees to be bound by the terms and conditions of this Agreement by and between
            Customer and Orion Advisor Technology, LLC, a Nebraska limited liability company
            (“Orion”).
          </p>

          <p>
            <strong>WHEREAS</strong>, Orion, or an affiliate of Orion, and Customer have entered
            into a services agreement (as the same is or may be amended, the “Existing Services
            Agreement”);
          </p>

          <p>
            <strong>WHEREAS</strong>, Orion, through its Client Portal service, offers a behavioral
            finance assessment, designed to drive business growth and conversations with Customer
            clients and prospective clients (the “BeFi20 Assessment Tool”);
          </p>

          <p>
            <strong>WHEREAS</strong>, Customer desires to utilize the BeFi20 Assessment Tool and
            Orion has agreed to make the BeFi20 Assessment Tool available to Customer pursuant to
            the terms and conditions set forth herein; and
          </p>

          <p>
            <strong>WHEREAS</strong>, capitalized terms used in this Agreement but not defined
            herein shall have the meanings given to them in the Existing Services Agreement.
          </p>

          <p>
            <strong>NOW, THEREFORE</strong>, in consideration of the foregoing and the agreements
            contained herein, and for other good and valuable consideration, the receipt and
            adequacy of which are hereby acknowledged, the parties hereto agree as follows:
          </p>

          <ol>
            <li>
              <strong>Description</strong>
            </li>
            <ol type="a">
              <li>
                <strong>BeFi20</strong>. Orion’s Orion Planning and Client Portal functionalities,
                available via the Existing Services Agreement, now allow for access to the BeFi20
                Assessment Tool for use with Customer’s clients and prospective clients, and friends
                and family of the same, subject to the terms and conditions contained herein or
                embedded in the tool. Customer further acknowledges, understands, and agrees that
                its use of the data aggregation functionality within the Orion Planning tool shall
                be subject to the additional terms and conditions found at
                https://orion.com/download/third-party-data-terms-and-conditions/, as maintained and
                updated by Orion from time to time, and incorporated herein by this reference.
                Customer’s use of such data aggregation functionality within the Orion Planning tool
                shall be subject to a fee of ten dollars ($10.00) per household, per annum, and such
                fee will be invoiced to Customer quarterly in arrears.
              </li>
              <li>
                <strong>Assessment Sharing Functionality</strong>. As part of the BeFi20 Assessment
                Tool offering, Customer acknowledges and agrees that such functionality is driven by
                the sharing of such information and results generated by the assessment between
                Advisor and Advisor’s client(s) and prospective client(s), but also between such
                clients or prospective clients and their respective friends and family members and
                extensions downstream of such sharing. Accordingly, Customer hereby releases and
                agrees to hold Orion harmless from all claims, liabilities, losses, damages,
                attorney’s fees, and expenses of any kind resulting from (i) the release of and
                providing access to the BeFi20 Assessment Tool, and the information gathered
                pursuant to such tool, through the assessment sharing functionality of the tool,
                (ii) Customer’s, and its users’, actions or inactions with respect to the BeFi20
                Assessment tool, and (iii) actions taken by Orion in reliance on communications and
                instructions received from Customer and its users in relation to their use of the
                BeFi20 Assessment Tool.
              </li>
            </ol>

            <li>
              <strong>Content; Ownership; Restrictions</strong>. Customer acknowledges and agrees
              that Customer may have access to content which is the property of Orion, third
              parties, or their respective licensors, and protected by intellectual property laws.
              Customer agrees not to further license, copy, sell, rent, lease, distribute, or create
              derivative works based on such content or to remove any ownership, copyright, or
              trademark designation on such content, except as expressly authorized in writing.
            </li>

            <li>
              <strong>Fees</strong>. Access to the BeFi20 Assessment Tool shall be complimentary to
              the Services set forth in the Existing Services Agreement and no additional fees to
              the Service Fees set forth therein shall be due and payable pursuant hereto. Customer
              agrees to pay the Service Fees as set forth in the Existing Services Agreement.
            </li>

            <li>
              <strong>Representations and Warranties</strong>. Customer represents, warrants, and
              covenants the following:
            </li>

            <ol type="a">
              <li>
                Customer is duly organized and validly existing under the laws of the state of its
                organization and has the necessary authority to enter into this Agreement and
                perform its duties and obligations hereunder.
              </li>

              <li>
                The acknowledgment of this Agreement by Customer and the performance of its duties
                and obligations hereunder has been duly authorized by all necessary corporate or
                other action on behalf of Customer and does not conflict with or constitute a breach
                or default under its organization documents or the terms and conditions of any other
                agreement or other document to which it is a party or any order, decree, law, or
                government regulation to which it is subject.
              </li>

              <li>
                Customer shall comply with all federal and state securities laws and rules that are
                applicable to Customer throughout the term of this Agreement.
              </li>

              <li>
                Customer shall obtain any required consents, licenses, and approvals for its use of
                the BeFi20 Assessment Tool including, but not limited to, the provision of data from
                third parties, third party licenses necessary for the exporting of data, and
                regulatory approvals.
              </li>

              <li>
                Customer acknowledges and agrees that Customer is ultimately responsible for
                activities performed under this Agreement. Customer agrees that Customer remains
                entirely responsible for Customer’s investment advisory services and understands
                that Orion is not providing any advice or recommendations or agreeing to enter into
                any relationship with respect to Customer’s clients or prospects.
              </li>

              <li>
                Customer shall: (i) comply with all applicable laws and regulations, including, but
                not limited to, data privacy and security throughout the term of this Agreement;
                (ii) maintain appropriate technical and organizational measures to protect personal
                data against accidental or unlawful destruction, loss, alteration, unauthorized
                disclosure of, or access to, data; (iii) notify Orion in accordance with applicable
                law upon becoming aware of any accidental, unlawful, or unauthorized destruction,
                loss, alteration, theft, disclosure, exfiltration, or access involving personal
                data; (iv) make reasonable efforts to identify the cause of any such data breach;
                and (v) take steps reasonably necessary to remediate the cause of such data breach.
              </li>
            </ol>

            <li>
              <strong>Disclaimer</strong>.
            </li>

            <ol type="a">
              <li>
                Customer acknowledges and agrees that none of the data, services, content, or
                technology made available to Customer under this Agreement constitutes: (i) a
                recommendation, endorsement, offer, or solicitation for any investment advisor,
                investment manager, or other investment firm or its services; (ii) an offer,
                purchase, or sale of any security, investment product, or service; (iii) legal,
                investment, tax, or fiduciary advice; or (iv) any guarantee of compliance with laws,
                statutes, rules, or regulations of the U.S. Securities and Exchange Commission
                (“SEC”), Financial Industry Regulatory Authority (“FINRA”), U.S. Department of Labor
                (“DOL”), or any other applicable local, state, federal, or international regulator.
                Any financial or investment information made available to Customer, as well as
                reference materials or links to other web sites, are for general informational
                purposes only, and are not intended to be relied upon for transactional or other
                purposes. Past performance of any security, investment manager, financial plan, or
                strategy is not an indication of future performance.
              </li>

              <li>
                Customer acknowledges and agrees that neither Orion nor any of its respective
                employees, agents, managers, directors, affiliates, independent contractors,
                services providers, or third parties shall be deemed to offer advice regarding the
                quality or suitability of any investment or of any investment manager for Customer
                and assume no responsibility or liability for any action, investment decisions,
                advice, treatment, or services rendered by Customer. Customer expressly agrees that
                the BeFi Assessment Tool services and access, including access to Orion’s Orion
                Planning tools does not provide Customer or Customer’s clients or prospective
                clients with any financial or investment advice, and that any such action Customer
                or Customer’s user, clients or prospective clients take in reliance on such service
                offerings are at such person’s own discretion.
              </li>

              <li>
                Customer acknowledges and agrees that Orion and its respective employees, agents,
                managers, directors, affiliates, independent contractors, service providers, or
                third parties shall not have any liability for investment recommendations or
                decisions made by Customer, its clients, or prospects based upon, or the results
                obtained from, Customer’s use of the BeFi20 Assessment Tool.
              </li>

              <li>
                Customer acknowledges and agrees that Customer is solely responsible for obtaining
                and/or verifying FINRA, SEC, DOL, or other regulatory or compliance approvals
                necessary for Customer’s use of the BeFi20 Assessment Tool and any data, services,
                content, or technology utilized by Customer, as may be applicable or required.
                Neither Orion nor any of its respective employees, agents, directors, managers,
                independent contractors, service providers, or third parties make any representation
                or warranty that the BeFi20 Assessment Tool or any data, content, or technology made
                available to Customer comply with state, federal, or international rules or
                regulations, and has no direct obligation with respect to regulatory compliance and
                disclaims all representations, warranties, and liability associated therewith.
              </li>

              <li>
                CUSTOMER ACKNOWLEDGES THAT CUSTOMER IS ENGAGED IN AN INDUSTRY THAT IS REGULATED BY
                MULTIPLE STATE AND LOCAL REGULATORY BODIES. ORION HEREBY DISCLAIMS ALL
                RESPONSIBILITY FOR CUSTOMER’S COMPLIANCE WITH THEIR FIRM’S COMPLIANCE AND
                SUPERVISION POLICIES, APPLICABLE FEDERAL, STATE, LOCAL, AND INDUSTRY REGULATORY
                BODIES. CUSTOMER WILL INDEMNIFY ORION AND HOLD ORION HARMLESS FROM AND AGAINST ANY
                LOSS, COST, DAMAGE, OR EXPENSE, INCLUDING WITHOUT LIMITATION LEGAL FEES AND
                EXPENSES, WITH RESPECT TO ANY CLAIM OR ACTION BROUGHT AGAINST ORION BY A REGULATORY
                BODY OR THIRD PARTY BASED ON CUSTOMER’S USE OR MISUSE OF THE BEFI20 ASSESSMENT TOOL
                OR THE CONTENT GENERATED OUT OF THE BEFI20 ASSESSMENT TOOL.
              </li>

              <li>
                Customer acknowledges and agrees that the BeFi20 Assessment Tool is being provided
                “as is”, without any warranty or representation regarding its availability,
                functionality, or results. Customer further acknowledges and agrees that Orion shall
                not be liable for the accuracy, timeliness, or completeness of any data, services,
                content, or technology furnished under this Agreement or for any delays,
                interruptions, omissions, lost profits, direct, indirect, special, or consequential
                damages. Customer understands and agrees that the third-party product offering
                aspect of the Services is subject to the availability of such third party offerings,
                that such services are not under Orion’s control, and Orion shall not be responsible
                for any liability, damage, or loss arising from or in connection with Customer’s use
                of such third-party products or services. Customer further acknowledges that
                Customer’s data may be shared with such third-party partners with Customer’s consent
                (which shall be deemed granted by execution hereof), and as necessary to allow
                Customer to utilize such third-parties’ services.
              </li>
            </ol>

            <li>
              <strong>Term and Termination</strong>.
            </li>

            <ol type="a">
              <li>
                This Agreement shall become effective on the Effective Date and will continue until
                terminated as provided in the Existing Services Agreement or herein. This Agreement
                and the services provided hereunder may be terminated by Customer’s selection within
                the Client Portal application. Orion specifically reserves the right to terminate
                this Agreement and/or access to the BeFi20 Assessment Tool as provided in the
                Existing Services Agreement.
              </li>

              <li>
                This Agreement may be terminated by either party in the event of a material breach
                by the other. In the event of a material breach, the non-breaching party shall give
                the other party notice describing the breach and thirty (30) days to cure the
                breach. In the event that the breach is not cured within such time period, this
                Agreement shall be deemed terminated without further notice. In the event a material
                breach is due to Customer’s failure to pay outstanding amounts due under this
                Agreement or the Existing Services Agreement by their due date, Customer
                acknowledges and agrees that its access to the BeFi20 Assessment Tool may be
                disabled without any prior notice and may remain disabled until all outstanding
                amounts due hereunder are fully paid. Notwithstanding the foregoing, this Agreement
                shall automatically terminate concurrently with the Existing Services Agreement.
              </li>

              <li>
                Upon termination of this Agreement, Orion shall have no further obligation to
                provide services to Customer. The provisions of Sections 4, 5, and 7 hereof shall
                survive the termination or expiration of this Agreement pursuant to their terms. In
                the event of termination, there shall be no refund of any fees paid pursuant hereto,
                including any setup fees.
              </li>
            </ol>

            <li>
              <strong>Intellectual Property</strong>. Orion hereby grants to Customer a
              non-exclusive and non-transferable license (the “License”) to access and utilize the
              BeFi20 Assessment Tool as described in this Agreement. Customer hereby agrees that
              neither Orion nor any third party licensor providing information to Orion shall have
              any liability for the accuracy or completeness of the information or technology
              furnished through the License or for delays, interruptions, or omissions therein, nor
              for any lost profits, direct, indirect, special or consequential damages. Customer
              further agrees (i) not to use or permit anyone to use the information or technology
              provided through the License for any unlawful or unauthorized purpose; (ii) not to
              furnish such information or technology to any person or firm for reuse or
              retransmission without prior written approval from Orion; and (iii) access to some of
              the services is subject to termination in the event that Orion is unable to obtain
              necessary information from its providers.
            </li>

            <li>
              Orion reserves the right to amend this Agreement at any time by posting the amended
              terms to this site. You should review this Agreement from time to time, as your
              continued use of the BeFi20 Assessment Tool signifies your acceptance of any changes.
              If you do not agree with this Agreement, your sole and exclusive remedy is to
              discontinue using the BeFi20 Assessment Tool.
            </li>
          </ol>

          <h3>Acknowledgement and Agreement:</h3>
          <p>
            The parties hereby agree and acknowledge that this Agreement is intended to supplement
            the terms and conditions contained in the Existing Services Agreement. Except as
            specifically modified hereby, the Existing Services Agreement shall remain in full force
            and effect.
          </p>
        </StyledText>
      </ModalBody>

      <ModalFooter hasBorder>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button ml="8" onClick={onAccept}>
          I Accept
        </Button>
      </ModalFooter>
    </Modal>
  );
};
